import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import QRCode from 'qrcode';
import { getRealtorDetail, updateUserStatus, getAllRealtors } from '../../../services/memberService';
import { getCommissionSummary } from '../../../services/commissionService';
import { getWithdrawalSummary, getWithdrawalsByRealtor } from '../../../services/withdrawalService';
import { formatBusinessNumber, formatDateDigits } from '../../../services/partnerLicenseVerification';

const STATUS_LABEL = { active: '활성화', pending: '승인 대기', suspended: '정지', inactive: '비활성화' };

// 자동검증 결과 → 경고 뱃지 (폐업/미등록만 표시, 통과는 안 보임)
const NTS_WARNING = ({ data }) => {
  const st = data.auto_verification_status;
  if (!st || st === 'ocr_auto_approved' || st === 'ocr_processing' || st === 'manual_pending') return null;
  const text = data.nts_b_stt && data.nts_b_stt !== '계속사업자'
    ? `⚠ ${data.nts_b_stt}`
    : st === 'ocr_auto_rejected' ? '⚠ 국세청 검증 실패'
    : st === 'ocr_review_required' ? '⚠ 확인 필요'
    : '';
  if (!text) return null;
  return (
    <span style={{
      marginLeft: 8,
      fontSize: 11,
      padding: '2px 8px',
      borderRadius: 999,
      background: st === 'ocr_auto_rejected' ? '#FEE2E2' : '#FEF3C7',
      color: st === 'ocr_auto_rejected' ? '#DC2626' : '#D97706',
      fontWeight: 600,
    }}>
      {text}
    </span>
  );
};

// 사업자번호 가운데 2자리로 개인/법인 판별
const detectBusinessType = (bizNo) => {
  const d = String(bizNo || '').replace(/\D/g, '');
  if (d.length !== 10) return '';
  const mid = parseInt(d.slice(3, 5), 10);
  if (mid >= 80) return '법인';
  return '개인';
};

const Wrap = styled.div``;
const BackBtn = styled.button`
  background: none; border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 13px; cursor: pointer; margin-bottom: 12px; padding: 0;
  &:hover { text-decoration: underline; }
`;

const TopRow = styled.div`
  display: flex; justify-content: space-between; align-items: flex-end;
  margin-bottom: 4px;
`;
const Title = styled.h1` font-size: 18px; font-weight: 600; `;
const SubText = styled.p`
  font-size: 13px; color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 20px;
`;
const RefreshBtn = styled.button`
  background: none; border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 13px; cursor: pointer;
`;

const Grid3 = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  margin-bottom: 20px;
  @media (max-width: 1024px) { grid-template-columns: 1fr; }
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 10px;
  padding: 20px 22px;
`;
const CardTitle = styled.h3`
  font-size: 14px; font-weight: 600; margin-bottom: 16px;
  color: #1F2937;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ $cols }) => $cols || 2}, 1fr);
  gap: 14px 28px;
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

const InfoItem = styled.div``;
const InfoLabel = styled.div`
  font-size: 12px; color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 4px;
`;
const InfoValue = styled.div`
  font-size: 13px; font-weight: 500;
  color: #1F2937;
  word-break: break-all;
`;

const SubTitleSmall = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: -10px;
  margin-bottom: 14px;
`;
const DataTable = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
`;
const DataRow = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  min-height: 38px;
  align-items: center;
`;
const DataLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: 8px 12px;
  background: #FAFAFA;
  font-weight: 500;
  align-self: stretch;
  display: flex;
  align-items: center;
`;
const DataValue = styled.div`
  font-size: 13px;
  color: #1F2937;
  padding: 8px 14px;
  word-break: break-all;
`;
const StatusDot = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $color }) => $color || '#9CA3AF'};
  margin-right: 6px;
  vertical-align: middle;
`;
const Muted = styled.span`color:#9CA3AF;font-style:italic;`;

const StatusSelect = styled.select`
  padding: 6px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  font-size: 13px;
  background: #fff;
`;

const WithdrawRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
`;
const Stat = styled.div`
  padding: 12px 14px;
  background: #F9FAFB;
  border-radius: 8px;
  text-align: center;
`;
const StatLabel = styled.div`
  font-size: 12px; color: #6B7280; margin-bottom: 4px;
`;
const StatValue = styled.div`
  font-size: 16px; font-weight: 600;
  color: ${({ $c }) => $c || '#1F2937'};
`;

const formatDate = (v) => {
  if (!v) return '-';
  if (typeof v === 'string') return v.slice(0, 10);
  try {
    let d;
    if (v.toDate) d = v.toDate();
    else if (v.seconds) d = new Date(v.seconds * 1000);
    else d = new Date(v);
    if (isNaN(d.getTime())) return '-';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  } catch { return '-'; }
};
const formatWon = (n) => `₩${(n || 0).toLocaleString()}`;

const RealtorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comm, setComm] = useState({ total: 0, settled: 0, conversion: 0, referral: 0 });
  const [withdraw, setWithdraw] = useState({ totalWithdrawn: 0, pendingCount: 0 });
  const [pendingAmount, setPendingAmount] = useState(0);
  const [referrer, setReferrer] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const detail = await getRealtorDetail(id);
      setData(detail);

      const c = await getCommissionSummary(id).catch(() => null);
      if (c) setComm(c);

      const w = await getWithdrawalSummary(id).catch(() => null);
      if (w) setWithdraw(w);

      // 출금 대기 금액 합산
      try {
        const allW = await getWithdrawalsByRealtor(id);
        const pending = allW
          .filter((x) => x.status === 'requested' || x.status === 'approved')
          .reduce((a, b) => a + (b.amount || 0), 0);
        setPendingAmount(pending);
      } catch { /* skip */ }

      // 추천인 정보
      if (detail?.referrer_id) {
        try {
          const realtors = await getAllRealtors();
          const r = realtors.find((x) => x.uid === detail.referrer_id);
          if (r) setReferrer(r);
        } catch { /* skip */ }
      }
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (status) => {
    if (!window.confirm(`상태를 "${STATUS_LABEL[status]}" 으로 변경하시겠습니까?`)) return;
    try {
      await updateUserStatus(id, status);
      setData((prev) => {
        const next = { ...prev, status };
        if (status === 'active' && !prev?.approved_at) {
          next.approved_at = { seconds: Math.floor(Date.now() / 1000) };
        }
        return next;
      });
    } catch (err) { alert(err.message || '변경 실패'); }
  };

  const [qrModal, setQrModal] = useState(null); // { url, dataUrl }

  const viewQr = async () => {
    // uid 기반 추천 링크로 동적 QR 생성 (사전 저장된 qr_code_url 없어도 표시 가능)
    const uid = id;
    if (!uid) return alert('부동산 ID 없음');
    const formUrl = `https://ieum-landing.web.app/apply?ref=${uid}`;
    try {
      const dataUrl = await QRCode.toDataURL(formUrl, { width: 360, margin: 2 });
      setQrModal({ url: formUrl, dataUrl });
    } catch (e) { alert('QR 생성 실패: ' + (e.message || e)); }
  };

  const downloadQr = () => {
    if (!qrModal?.dataUrl) return;
    const a = document.createElement('a');
    a.href = qrModal.dataUrl;
    a.download = `ieum-realtor-${id}.png`;
    a.click();
  };

  if (loading) return <Wrap><p>로딩 중...</p></Wrap>;
  if (!data) return <Wrap><p>부동산 정보를 찾을 수 없습니다.</p></Wrap>;

  const bank = data.bank_name || data.account_bank;
  const accountType = data.account_type || (data.business_number ? '사업자' : '개인');

  return (
    <Wrap>
      <BackBtn onClick={() => navigate('/admin/members/realtors')}>← 목록으로</BackBtn>

      <TopRow>
        <div>
          <Title>공인중개사 상세</Title>
        </div>
        <RefreshBtn onClick={load}>새로고침</RefreshBtn>
      </TopRow>
      <SubText>등록된 모든 정보를 확인할 수 있습니다.</SubText>

      {/* 기본 정보 — 표준 12필드 카드 */}
      <Card style={{ marginBottom: 20 }}>
        <CardTitle>기본 정보</CardTitle>
        <SubTitleSmall>사업자 정보와 담당자 정보를 표준 카드로 표시합니다.</SubTitleSmall>
        <DataTable>
          <DataRow>
            <DataLabel>업체명</DataLabel>
            <DataValue>{data.ocr_business_name || data.businessName || data.business_name || '-'}</DataValue>
          </DataRow>
          <DataRow>
            <DataLabel>대표자</DataLabel>
            <DataValue>{data.ocr_representative_name || data.owner_name || data.name || '-'}</DataValue>
          </DataRow>
          <DataRow>
            <DataLabel>사업자번호</DataLabel>
            <DataValue>
              {formatBusinessNumber(data.ocr_business_number || data.business_number || data.biz_number) || '-'}
              <NTS_WARNING data={data} />
            </DataValue>
          </DataRow>
          <DataRow>
            <DataLabel>사업자 유형</DataLabel>
            <DataValue>
              {(() => {
                if (data.user_type === 'personal') return '개인';
                const t = data.ocr_business_type || data.business_type;
                if (t === 'corporation') return '법인';
                if (t === 'sole_proprietor') return '개인';
                return detectBusinessType(data.ocr_business_number || data.business_number) || '-';
              })()}
            </DataValue>
          </DataRow>
          <DataRow>
            <DataLabel>개업일자</DataLabel>
            <DataValue>{(data.ocr_open_date || data.open_date) ? formatDateDigits(data.ocr_open_date || data.open_date) : '-'}</DataValue>
          </DataRow>
          <DataRow>
            <DataLabel>주소</DataLabel>
            <DataValue>{data.ocr_address || data.address || '-'}</DataValue>
          </DataRow>
          <DataRow>
            <DataLabel>대표 연락처</DataLabel>
            <DataValue>{data.business_phone || data.phone || '-'}</DataValue>
          </DataRow>
          <DataRow>
            <DataLabel>담당자</DataLabel>
            <DataValue>{data.name || '-'}</DataValue>
          </DataRow>
          <DataRow>
            <DataLabel>담당자 연락처</DataLabel>
            <DataValue>{data.manager_phone || data.phone || '-'}</DataValue>
          </DataRow>
          <DataRow>
            <DataLabel>담당자 이메일</DataLabel>
            <DataValue>{data.email || '-'}</DataValue>
          </DataRow>
          <DataRow>
            <DataLabel>가입승인 일자</DataLabel>
            <DataValue>{formatDate(data.approved_at) !== '-' ? formatDate(data.approved_at) : <Muted>미승인</Muted>}</DataValue>
          </DataRow>
          <DataRow>
            <DataLabel>상태</DataLabel>
            <DataValue>
              <StatusDot $color={
                data.status === 'active' ? '#22C55E'
                : data.status === 'pending' ? '#F59E0B'
                : data.status === 'suspended' ? '#EF4444'
                : '#9CA3AF'
              } />
              <StatusSelect value={data.status || 'inactive'} onChange={(e) => handleStatusChange(e.target.value)}>
                <option value="active">활성화</option>
                <option value="pending">승인 대기</option>
                <option value="suspended">정지</option>
                <option value="inactive">비활성화</option>
              </StatusSelect>
            </DataValue>
          </DataRow>
        </DataTable>
      </Card>

      {/* 수익 + 추천인 + 계좌 */}
      <Grid3>
        <Card>
          <CardTitle>수익 및 출금 현황</CardTitle>
          <InfoGrid $cols={1}>
            <InfoItem><InfoLabel>전환 수수료</InfoLabel><InfoValue>{formatWon(comm.conversion)}</InfoValue></InfoItem>
            <InfoItem><InfoLabel>상담 수수료</InfoLabel><InfoValue>{formatWon((comm.total || 0) - (comm.conversion || 0) - (comm.referral || 0))}</InfoValue></InfoItem>
            <InfoItem><InfoLabel>추천 수수료</InfoLabel><InfoValue>{formatWon(comm.referral)}</InfoValue></InfoItem>
          </InfoGrid>
          <div style={{ marginTop: 16 }}>
            <WithdrawRow>
              <Stat>
                <StatLabel>대기/승인</StatLabel>
                <StatValue $c="#F59E0B">{formatWon(pendingAmount)}</StatValue>
              </Stat>
              <Stat>
                <StatLabel>출금 대기건</StatLabel>
                <StatValue>{withdraw.pendingCount}건</StatValue>
              </Stat>
              <Stat>
                <StatLabel>완료 금액</StatLabel>
                <StatValue $c="#22C55E">{formatWon(withdraw.totalWithdrawn)}</StatValue>
              </Stat>
            </WithdrawRow>
          </div>
        </Card>

        <Card>
          <CardTitle>추천인</CardTitle>
          {referrer ? (
            <InfoGrid $cols={1}>
              <InfoItem><InfoLabel>추천 업체</InfoLabel><InfoValue>{referrer.businessName || referrer.business_name || '-'}</InfoValue></InfoItem>
              <InfoItem><InfoLabel>추천인 담당자</InfoLabel><InfoValue>{referrer.name || '-'}</InfoValue></InfoItem>
              <InfoItem><InfoLabel>추천 완료일</InfoLabel><InfoValue>{formatDate(data.createdAt)}</InfoValue></InfoItem>
            </InfoGrid>
          ) : (
            <div style={{ color: '#9CA3AF', fontSize: 13, padding: '16px 0', textAlign: 'center' }}>
              추천인 없음
            </div>
          )}
        </Card>

        <Card>
          <CardTitle>정산 계좌</CardTitle>
          <InfoGrid $cols={1}>
            <InfoItem><InfoLabel>계좌 유형</InfoLabel><InfoValue>{accountType}</InfoValue></InfoItem>
            <InfoItem><InfoLabel>은행</InfoLabel><InfoValue>{bank || '-'}</InfoValue></InfoItem>
            <InfoItem><InfoLabel>계좌번호</InfoLabel><InfoValue>{data.account_number || '-'}</InfoValue></InfoItem>
            <InfoItem><InfoLabel>예금주</InfoLabel><InfoValue>{data.account_holder || '-'}</InfoValue></InfoItem>
            <InfoItem>
              <InfoLabel>계좌 인증</InfoLabel>
              <InfoValue style={{ color: data.accountVerified ? '#22C55E' : '#EF4444' }}>
                {data.accountVerified ? '인증완료' : '미인증'}
              </InfoValue>
            </InfoItem>
          </InfoGrid>
        </Card>
      </Grid3>

      {/* 서류/첨부 + QR */}
      <Card>
        <CardTitle>서류 / 부가정보</CardTitle>
        <InfoGrid $cols={2}>
          <InfoItem>
            <InfoLabel>사업자등록증</InfoLabel>
            <InfoValue>
              {data.license_url ? (
                <a href={data.license_url} target="_blank" rel="noreferrer" style={{ color: '#4A6CF7' }}>
                  {data.license_file_name || '파일 보기'} ↗
                </a>
              ) : '-'}
            </InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>초대 QR</InfoLabel>
            <InfoValue>
              <button onClick={viewQr} style={{ color: '#4A6CF7', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 13 }}>
                QR 보기 ↗
              </button>
            </InfoValue>
          </InfoItem>
        </InfoGrid>
      </Card>

      {qrModal && (
        <div
          onClick={() => setQrModal(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{
            background: '#fff', borderRadius: 12, padding: 24, maxWidth: 420, width: '90%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>초대 QR</div>
            <img src={qrModal.dataUrl} alt="invite-qr" style={{ width: '100%', display: 'block', borderRadius: 8 }} />
            <div style={{ fontSize: 12, color: '#64748B', marginTop: 12, wordBreak: 'break-all' }}>
              {qrModal.url}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button onClick={downloadQr} style={{
                flex: 1, padding: '10px 16px', background: '#4A6CF7', color: '#fff',
                border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600,
              }}>다운로드</button>
              <button onClick={() => setQrModal(null)} style={{
                flex: 1, padding: '10px 16px', background: '#F1F5F9', color: '#334155',
                border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600,
              }}>닫기</button>
            </div>
          </div>
        </div>
      )}
    </Wrap>
  );
};

export default RealtorDetail;
